// ============================================
// src/services/NetlifyAIGenerator.js
// Simple AI Generator for Netlify deployment
// ============================================

class NetlifyAIGenerator {
  constructor() {
    // You can add API keys here later if needed
    this.huggingFaceKey = process.env.REACT_APP_HUGGINGFACE_KEY || null;
  }

  // Smart template-based generation (Always works, no dependencies)
  generateWithSmartTemplates(data) {
    const { amount, month, year, paidCount, pendingCount, flatDetails, gpay, style = 'standard' } = data;
    
    const templates = {
      standard: [
        `The monthly maintenance charge of ₹${amount} started in ${month} ${year}.
Payment Received Details!
${flatDetails}
Payment can be made via GPay to ${gpay}`,
      ],
      
      friendly: [
        `Hi everyone! 👋\n\nGentle reminder: Our monthly maintenance of ₹${amount} for ${month} is due.\n\n${paidCount} flats have paid, ${pendingCount} pending:\n${flatDetails}\n\nPayment via GPay: ${gpay}\n\nThank you! 😊`,
        `Hello neighbors!\n\nQuick update on ${month}'s maintenance (₹${amount}):\n\n${flatDetails}\n\n${pendingCount > 0 ? `${pendingCount} payments pending. ` : 'Almost done! '}Please pay via GPay: ${gpay}`,
      ],
      
      urgent: [
        `⚠️ PAYMENT REMINDER\n\nMaintenance: ₹${amount} (${month} ${year})\nPending: ${pendingCount} flats\n\n${flatDetails}\n\nImmediate payment required.\nGPay: ${gpay}`,
        `URGENT: ${month} maintenance of ₹${amount} is overdue for ${pendingCount} flats.\n\n${flatDetails}\n\nPay immediately via GPay: ${gpay}`,
      ],
      
      professional: [
        `Dear Residents,\n\nThis is to remind you that the maintenance fee of ₹${amount} for ${month} ${year} is due.\n\nPayment Status:\n${flatDetails}\n\nPayment Details:\n• Amount: ₹${amount}\n• Method: GPay\n• Number: ${gpay}\n\nRegards,\nSociety Management`,
      ]
    };

    const selectedTemplates = templates[style] || templates.standard;
    const randomIndex = Math.floor(Math.random() * selectedTemplates.length);
    return selectedTemplates[randomIndex];
  }

  // Optional: Hugging Face API integration (if you want to add it later)
  async generateWithHuggingFaceAPI(prompt) {
    if (!this.huggingFaceKey) {
      console.log('No Hugging Face API key configured');
      return null;
    }

    try {
      const response = await fetch(
        'https://api-inference.huggingface.co/models/gpt2',
        {
          headers: { 
            Authorization: `Bearer ${this.huggingFaceKey}`,
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: JSON.stringify({ 
            inputs: prompt,
            parameters: {
              max_length: 150,
              temperature: 0.8
            }
          }),
        }
      );
      
      if (!response.ok) {
        console.error('API error:', response.status);
        return null;
      }
      
      const result = await response.json();
      return result.generated_text || result[0]?.generated_text || null;
    } catch (error) {
      console.error('Hugging Face API error:', error);
      return null;
    }
  }

  // Main method - generates message
  async generateMessage(data) {
    let result = null;
    
    // Try Hugging Face API if available
    if (this.huggingFaceKey && data.useAI) {
      const prompt = this.createPrompt(data);
      result = await this.generateWithHuggingFaceAPI(prompt);
    }
    
    // Always fallback to smart templates
    if (!result) {
      result = this.generateWithSmartTemplates(data);
    }
    
    return this.formatOutput(result, data);
  }

  createPrompt(data) {
    return `Create a WhatsApp group reminder for monthly maintenance payment.
Amount: ₹${data.amount}
Month: ${data.month} ${data.year}
Status: ${data.paidCount} paid, ${data.pendingCount} pending
Payment: GPay ${data.gpay}

Write a ${data.style || 'friendly'} reminder message. Keep it under 100 words.`;
  }

  formatOutput(message, data) {
    if (!message) return this.generateWithSmartTemplates(data);
    
    // Ensure payment details are included if missing
    if (!message.includes(data.flatDetails) && data.flatDetails) {
      message += `\n\n${data.flatDetails}`;
    }
    
    if (!message.includes(data.gpay)) {
      message += `\n\nPay via GPay: ${data.gpay}`;
    }
    
    return message.trim();
  }
}

export default new NetlifyAIGenerator();